'use client'

import { Slideshow } from '@fukku/slideshow'
import { SlideshowAnimation } from '@fukku/slideshow/types/Slideshow'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import SmartCropImage from 'media/components/SmartCropImage/SmartCropImage'
import type { ProductImage } from 'product/types'
import { dataTestIds } from 'product-card/constants/dataTestIds'
import { useAnimationImagesLoad } from 'product-card/hooks/useAnimationImagesLoad'
import { useProductCard } from 'product-card/hooks/useProductCard'
import { useProductCardAnimationContext } from 'product-card/hooks/useProductCardAnimationContext'
import { useProductCardConsumerLayoutContext } from 'product-card/hooks/useProductCardConsumerLayoutContext'

import { useProductImageSizes } from 'product-card/hooks/useProductImageSizes/useProductImageSizes'
import { useShowAsFeatured } from 'product-card/hooks/useShowAsFeatured/useShowAsFeatured'
import { ProductCardAnimation } from 'product-card/types'
import { useCallback, useMemo, useRef } from 'react'
import { cx } from 'utils/cx'
import { some } from 'utils/some/some'
import styles from '../ProductImage.module.scss'

interface ProductImageSlideshowProps {
	readonly images: ProductImage[]
}

const FIRST_IMAGE = 0
const SECOND_IMAGE = 1

export const ProductImageSlideshow = ({
	images,
}: ProductImageSlideshowProps) => {
	const { dataTestId, draggable, priority } = useProductCard()

	const { animation, onAnimationEnd } = useProductCardAnimationContext()

	const { hasDotsIndicator } = useProductCardConsumerLayoutContext()

	const firstImageRef = useRef<HTMLImageElement | null>(null)

	const secondImageRef = useRef<HTMLImageElement | null>(null)

	const { isFirstImageReady, areImagesReadyForAnimation, handleImageLoaded } =
		useAnimationImagesLoad(images, secondImageRef)

	const showAsFeatured = useShowAsFeatured()

	const { t } = useLabels()

	const sizes = useProductImageSizes()

	const getImagePriority = useCallback(
		(index: number) => {
			return some(
				priority && index === FIRST_IMAGE,
				animation !== ProductCardAnimation.FadeIn &&
					[FIRST_IMAGE, SECOND_IMAGE].includes(index)
			)
		},
		[animation, priority]
	)

	const getImageRef = useCallback((index: number) => {
		if (index === FIRST_IMAGE) {
			return firstImageRef
		}
		if (index === SECOND_IMAGE) {
			return secondImageRef
		}
		return null
	}, [])

	// Apply fade in animation if there is any animation defined
	const fadeInAnimation = isFirstImageReady ? styles.fadeIn : styles.invisible

	const initialAnimation = useMemo<SlideshowAnimation | undefined>(() => {
		if (!areImagesReadyForAnimation) {
			return undefined
		}
		if (animation === ProductCardAnimation.ShortSlideshow) {
			return SlideshowAnimation.Short
		}
		if (animation === ProductCardAnimation.LongSlideshow) {
			return SlideshowAnimation.Long
		}
		return undefined
	}, [areImagesReadyForAnimation, animation])

	return (
		<div
			className={cx(styles.productImage, fadeInAnimation)}
			data-testid='productCard.images'
		>
			<Slideshow
				className={styles.slideShow}
				dataTestId={dataTestIds.productCardSlideshow(dataTestId)}
				hasPreload
				initialAnimation={initialAnimation}
				onAnimationEnd={onAnimationEnd}
				hasDotsIndicator={hasDotsIndicator}
				labels={{
					previous: t('accessibility.image.previous.button'),
					next: t('accessibility.image.next.button'),
				}}
			>
				{images?.map((image, index) => (
					<div key={image.alt} className={styles.imageWrapper}>
						<SmartCropImage
							ref={getImageRef(index)}
							data-testid={dataTestIds.productCardImage(dataTestId, index)}
							alt={image.alt}
							src={image.img}
							sizes={sizes}
							priority={getImagePriority(index)}
							showAsFeatured={showAsFeatured}
							fill
							draggable={draggable}
							onLoad={() => handleImageLoaded(image)}
						/>
					</div>
				))}
			</Slideshow>
		</div>
	)
}
